import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Scale, ArrowLeft, Loader2 } from 'lucide-react';
import { api } from '@/App';
import { toast } from 'sonner';

const SubmitCase = ({ user }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const categories = [
    'Personal Injury',
    'Employment Law',
    'Contract Dispute',
    'Property Law',
    'Family Law',
    'Consumer Rights',
    'Medical Malpractice',
    'Other'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.category) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    
    try {
      console.log('Submitting case:', formData);
      // Step 1: Create case (fast - returns immediately)
      const response = await api.post('/cases/submit', formData);
      console.log('Case created:', response.data);
      
      if (response.data && response.data.case_id) {
        toast.success('Case created! Redirecting to analysis...');
        // Navigate immediately with case data so page can show it right away
        navigate(`/case/${response.data.case_id}`, {
          state: {
            caseInfo: {
              title: formData.title,
              description: formData.description,
              category: formData.category,
              created_at: new Date().toISOString()
            }
          }
        });
      } else {
        console.error('Invalid response structure:', response.data);
        toast.error('Case submitted but unable to view results. Please check dashboard.');
        setTimeout(() => navigate('/dashboard'), 2000);
      }
    } catch (error) {
      console.error('Case submission error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      if (error.response) {
        toast.error(`Failed to submit case: ${error.response.data.detail || error.response.statusText}`);
      } else if (error.request) {
        toast.error('No response from server. Please check your connection and try again.');
      } else {
        toast.error('Failed to submit case. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Scale className="w-8 h-8 text-blue-700" />
            <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>LegalCheck</span>
          </div>
          
          <div className="flex items-center space-x-4">
            <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full border-2 border-blue-200" />
            <span className="text-slate-700 font-medium hidden sm:block">{user.name}</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <Button 
          data-testid="back-to-dashboard-button"
          onClick={() => navigate('/dashboard')}
          variant="outline"
          className="mb-8 rounded-full"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="mb-8">
          <h1 
            className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Submit Your Legal Case
          </h1>
          <p className="text-slate-600">
            Provide details about your legal situation for AI-powered analysis
          </p>
        </div>

        <Card className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title" className="text-slate-900 font-semibold">
                Case Title *
              </Label>
              <Input
                id="title"
                data-testid="case-title-input"
                placeholder="Brief title describing your case"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="rounded-lg border-slate-300"
                required
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category" className="text-slate-900 font-semibold">
                Case Category *
              </Label>
              <Select 
                data-testid="case-category-select"
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
                required
              >
                <SelectTrigger className="rounded-lg border-slate-300">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="text-slate-900 font-semibold">
                Case Description *
              </Label>
              <Textarea
                id="description"
                data-testid="case-description-textarea"
                placeholder="Describe your legal situation in detail. Include relevant dates, parties involved, and any supporting facts."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="rounded-lg border-slate-300 min-h-[200px]"
                required
              />
              <p className="text-xs text-slate-500">
                The more detail you provide, the better the AI analysis will be.
              </p>
            </div>

            {/* Disclaimer */}
            <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-xl">
              <p className="text-sm text-amber-900 font-medium">
                <strong>⚠️ Legal Disclaimer:</strong> This analysis is for informational purposes only and does not constitute legal advice. 
                The AI assessment should not be used as a substitute for consultation with a licensed attorney.
              </p>
            </div>

            {/* Submit Button */}
            <Button 
              data-testid="submit-case-button"
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-700 hover:bg-blue-800 text-white font-bold py-6 rounded-full text-lg shadow-xl hover:shadow-2xl transition-all duration-300"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Analyzing with AI...
                </>
              ) : (
                'Submit Case for Analysis'
              )}
            </Button>
          </form>
        </Card>
      </main>
    </div>
  );
};

export default SubmitCase;